import { flatten, groupBy, toPairs } from 'lodash'
import moment from 'moment'

import { IBookedTimeSlot, IRentableUnavailability, ILocation, IUnavailability } from './types'

const nameComparator = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'})

export interface IOption {
  id: number,
  name: string,
  slug: string,
  type: 'facility' | 'unit'
}

export interface ILocationOptions {
  id: number,
  name: string,
  options: IOption[]
}

export function getOptions(locations: ILocation[]): ILocationOptions[] {
  return locations
  .slice()
  .sort(nameComparator)
  .map(x => {
    return {
      id: x.id,
      name: x.name,
      options: flatten(
        x.facilities
          .slice()
          .sort(nameComparator)
          .map(x => {
            return [
              {
                id: x.id,
                name: x.name,
                slug: x.slug,
                type: 'facility' as const
              },
              ...(x.units || [])
                .sort(nameComparator)
                .map(x => ({
                  id: x.id,
                  name: x.name,
                  slug: x.slug,
                  type: 'unit' as const
                }))
            ]
          })
      )
    }
  })
}

export function groupBookedTimeSlots(booked: IRentableUnavailability[], selectedFacilities: string[]): IBookedTimeSlot[] {
  const result = toPairs(
    groupBy(
      booked.map(x => x.unavailabilities.map(u => ({
        ...u,
        id: x.id,
        type: x.type
      }))).flat(),
      x => x.date
    )
  )
  .map(([date, bookedItems]) => {
    if (selectedFacilities.every(x => bookedItems.find(bi => x === `${bi.id}-${bi.type}`))) {
      const intervals = toPairs(groupBy(bookedItems, bi => bi.id))
          .reduce((intersection, [, unavailabilities]) => {
            const currentIntervals = unavailabilities
              .map(u => [moment(u.start_time, 'HH:mm').unix(), moment(u.end_time, 'HH:mm').unix()]).sort((a ,b) => a[0] - b[0]) as TInterval[]
            if (!intersection) {
              return currentIntervals as TInterval[]
            }
            return findIntersection(
              intersection.sort((a ,b) => a[0] - b[0]),
              currentIntervals
            )
          }, undefined as TInterval[] | undefined)
      if (!intervals) {
        return []
      }
      
      return mergeIntervals(
        intervals
      )
      .map(x => {
        return {
          date,
          start_time: moment(x[0], 'X').format('HH:mm'),
          end_time: moment(x[1], 'X').format('HH:mm')
        }
      })
    }
    return []
  }).flat()

  return result
}

type TInterval = [number, number]

export const mergeIntervals = (intervals: TInterval[]) => {
  if (intervals.length < 2) return intervals;

  intervals.sort((a, b) => a[0] - b[0]);

  const result = [];
  let previous = intervals[0];

  for (let i = 1; i < intervals.length; i += 1) {
    if (previous[1] >= intervals[i][0]) {
      previous = [previous[0], Math.max(previous[1], intervals[i][1])];
    } else {
      result.push(previous);
      previous = intervals[i];
    }
  }

  result.push(previous);

  return result;
};

export const findIntersection = function (A: TInterval[], B: TInterval[]): TInterval[] {
  const res: TInterval[] = []
  let i = 0
  let j = 0
  while (i < A.length && j < B.length) {
    const [a, b] = A[i]
    const [c, d] = B[j]
    const lo = Math.max(a, c)
    const hi = Math.min(b, d)
    if (lo <= hi) {
      res.push([Math.max(a, c), Math.min(b, d)])
    }
    if (b < d) {
      i++
    } else {
      j++
    }
  }
  return res
};
